import React from 'react';
import { createRoot } from 'react-dom/client';

import { setupTemporalPolyfill } from '@myphx-engineering/date-utils/client';
import { makeFlagProvider } from '@myphx-engineering/feature-flags/client';
import { ThemeProvider } from '@myphx-engineering/phx-design-system/framework';
import { PwaProvider, type PwaProviderProps } from '@phx/pwa';

import { AppRouter } from './AppRouter';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { AppLoadedEventDispatcher } from './components/layout/AppLoadedEventDispatcher';
import RootErrorRoute from './components/router/RootErrorRoute';
import { RootSuspenseBoundary } from './components/utility/RootSuspenseBoundary';
import { AppConfigProvider } from './contexts/AppConfigProvider';
import { ErrorSessionProvider } from './contexts/ErrorSessionProvider';
import './i18n';
import theme from './theme';

setupTemporalPolyfill();

const pwaPath = '/static/pwa';
const pwaIllustrationsPath = `${pwaPath}/illustrations`;

const pwaConfig: PwaProviderProps = {
    pwaId: 'lilly',
    appName: 'LillyDirect',
    pwaAssets: {
        illustrations: {
            addToContacts: `${pwaIllustrationsPath}/add-to-contacts.svg`,
            addToContactsFromMenu: `${pwaIllustrationsPath}/add-to-contacts-from-menu.svg`,
            addToHome: `${pwaIllustrationsPath}/add-to-home.svg`,
            addToHomeFromMenu: `${pwaIllustrationsPath}/add-to-home-from-menu.svg`,
        },
        contactCard: {
            name: 'lilly-direct.vcf',
            url: `${pwaPath}/vcf/lilly-direct.vcf`,
        },
    },
};

export let flagProvider: ({
    children,
}: {
    children: import('react').ReactNode;
}) => import('react').JSX.Element;

(async () => {
    flagProvider = await makeFlagProvider(
        import.meta.env.VITE_LAUNCH_DARKLY_CLIENT_ID,
        undefined,
        import.meta.env.VITE_LAUNCH_DARKLY_BASE_URL
    );

    createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
            <ThemeProvider theme={theme}>
                <AppConfigProvider>
                    <ErrorSessionProvider>
                        <ErrorBoundary fallback={<RootErrorRoute p="md" />}>
                            <RootSuspenseBoundary>
                                <PwaProvider {...pwaConfig}>
                                    <AppRouter />
                                    <AppLoadedEventDispatcher />
                                </PwaProvider>
                            </RootSuspenseBoundary>
                        </ErrorBoundary>
                    </ErrorSessionProvider>
                </AppConfigProvider>
            </ThemeProvider>
        </React.StrictMode>
    );
})();
